import { Download } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import FileSaver from 'file-saver';
import { useMemo } from 'react';
import { RtCommonApiRoutes } from 'RtExports/routes';
import {
	RateSheetGenerationFileResponse,
	RateSheetGenerationIndexResponse
} from 'RtModels';
import { RateSheetGeneratorResource } from 'RtUi/app/AccountManagement/RateSheetGenerator/libs/resources/RateGeneratorResource';
import { useGetRateSheetGeneration } from 'RtUi/app/AccountManagement/RateSheetGenerator/libs/services';
import DataGrid from 'RtUi/components/data/DataGrid';
import { getRateSheetGenerationColumns } from 'RtUi/components/data/DataGrid/configurations/accountManagement/rateSheetGeneration';
import { handleGetRequest } from 'RtUi/utils/http/AxiosHttpRequest';

interface IRateSheetGenerationGridProps {
	autoFocusFilter?: boolean;
}

export const RateSheetGenerationDataGrid =
	({}: IRateSheetGenerationGridProps) => {
		const {
			data,
			isFetching: isLoading,
			refetch
		} = useGetRateSheetGeneration();
		const columns = useMemo(() => getRateSheetGenerationColumns(), []);

		const getFileData = (urlParams: RateSheetGenerationIndexResponse) => {
			return handleGetRequest<RateSheetGenerationFileResponse>(
				RtCommonApiRoutes.RateSheetGenerationFile.Profile,
				{
					urlParams: {
						rateSheetGenerationUuid: urlParams.rateSheetGenerationUuid
					}
				}
			);
		};

		const handleDownload = async (row: RateSheetGenerationIndexResponse) => {
			try {
				const fileData = await getFileData(row);
				if (!fileData.signedUrl) {
					throw new Error('File not found or not available for download.');
				}
				const response = await fetch(fileData.signedUrl, {
					method: 'GET',
					mode: 'cors',
					credentials: 'omit'
				});

				if (response.ok) {
					const blob = await response.blob();
					const blobUrl = URL.createObjectURL(blob);
					FileSaver.saveAs(blobUrl, fileData.fileName);
				} else {
					const text = await response.text();

					throw new Error(text);
				}
			} catch (e) {
				// TODO
			}
		};

		const resource = new RateSheetGeneratorResource();

		const inlineActions = (row: RateSheetGenerationIndexResponse) => (
			<Box sx={{ display: 'flex', justifyContent: 'left' }}>
				{Boolean(row.completedTs) && (
					<Tooltip title="Download file" arrow placement="right">
						<IconButton
							type="button"
							color="primary"
							size="small"
							onClick={(e) => {
								handleDownload(row);
								e.stopPropagation();
							}}
							sx={{
								padding: 0
							}}
						>
							<Download />
						</IconButton>
					</Tooltip>
				)}
			</Box>
		);

		return (
			<DataGrid<RateSheetGenerationIndexResponse>
				data={data?.data}
				columns={columns}
				totalRows={data?.totalCount}
				loading={isLoading}
				pageName={'RateSheetGeneration_generations'}
				inlineActions={inlineActions}
				positionActionsColumn="first"
			/>
		);
	};
